import type {
  CancelledFlight,
  FlightLeg,
  StandPreference,
  AirportConfig,
  Conflict,
  Disruption,
  Explanation,
  ExtendResource,
  OpsEvent,
  Plan,
  PlanScore,
  PredictedConflict,
  ResourceOverrides,
  StandAssignment,
  StandClosure,
  StandMove,
  Turn,
} from "@yb/core";
import type { CameraTurn } from "./apron";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Told when a signed-in call comes back 401: the session expired or was revoked by an
 * administrator. The app then re-reads the session, which is what puts the sign-in screen
 * up instead of a board that refuses everything.
 */
let signedOut: (() => void) | null = null;
export function onSignedOut(fn: (() => void) | null) {
  signedOut = fn;
}
/** Where a 401 is an answer to the person signing in, not a sign that they no longer are. */
const SIGNING_IN = /^\/api\/(login|demo-login|invite|mfa)(\/|\?|$)/;

/**
 * The sandbox the planning screens are looking at, or null for Live. Every request carries
 * it, so the board, the plan and Settings all answer for the same one; the server refuses
 * anything Live owns while it is set.
 */
let sandboxId: number | null = null;
export function setSandbox(id: number | null) {
  sandboxId = id;
}
export function currentSandbox(): number | null {
  return sandboxId;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const sb: Record<string, string> = sandboxId != null ? { "x-sandbox": String(sandboxId) } : {};
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: method === "GET" ? sb : { "content-type": "application/json", "x-yb": "1", ...sb },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && !SIGNING_IN.test(path)) signedOut?.();
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? res.statusText);
  return data as T;
}

export type Role = "superadmin" | "admin" | "planner" | "handler" | "airline" | "viewer" | "vendor";

/** One line of the change log: the audit trail in words (apps/server/src/changes.ts). */
export type ChangeKind = "plan" | "ops" | "settings" | "accounts" | "integrations" | "sign-in" | "guide" | "other";
export interface ChangeRow {
  id: number;
  at: string;
  user: string | null;
  kind: ChangeKind;
  action: string;
  text: string;
  detail: unknown;
  board?: { date: string; versionId: number | null };
  settingsVersion?: number;
}
export interface ConfigChange {
  path: string;
  before: unknown;
  after: unknown;
}
export interface SettingsDiff {
  id: number;
  note: string;
  by: string | null;
  at: string;
  previousId: number | null;
  changes: ConfigChange[];
  truncated: boolean;
}

/** What an API key may do; the same list as the server's SCOPES. */
export const SCOPES = ["read", "apron:write", "bags:write"] as const;
export type Scope = (typeof SCOPES)[number];

/**
 * The same lines as the server's auth.ts, so the board offers exactly what the API
 * accepts: a super administrator is an administrator, and administrators plan. A vendor
 * is an outside developer who sees nothing but the API page.
 */
export function isAdmin(role: Role): boolean {
  return role === "admin" || role === "superadmin";
}
export function canPlan(role: Role): boolean {
  return isAdmin(role) || role === "planner";
}
export function isVendor(role: Role): boolean {
  return role === "vendor";
}
export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  airline: string | null;
  /** For vendor accounts: the scopes their own keys may carry. Empty for everyone else. */
  apiScopes: Scope[];
}

export interface ClockState {
  mode: "replay" | "real";
  now: number;
  playing: boolean;
  speed: number;
}

export interface Board {
  airport: { code: string; iata: string; name: string; timezone: string };
  date: string;
  now: number;
  /** Set when this day is beyond the airport feed and was projected from that past day. */
  projectedFrom: string | null;
  clock: ClockState;
  feed: { kind: string; description: string; revision: string; lastUpdated: number | null };
  revision: string;
  turns: Turn[];
  /**
   * The neighbouring days' hours at each edge of the board. Shown so scrolling back before
   * midnight finds last night rather than an empty canvas; they belong to their own day's
   * plan and cannot be changed from here.
   */
  edges?: { turns: Turn[]; stands: Record<string, StandAssignment> };
  plan: Plan;
  overrides: ResourceOverrides;
  events: OpsEvent[];
  /** The C1 row: the day's cancelled flights, from the feed or cancelled here. */
  cancelled?: CancelledFlight[];
  closures: StandClosure[];
  conflicts: Conflict[];
  predictions: PredictedConflict[];
  score: PlanScore | null;
  version: { id: number; kind: string; summary: string; createdAt: string; createdBy: string | null; parentId: number | null };
}

export interface OptimizePreview {
  baseVersionId: number;
  before: { score: PlanScore; conflicts: Conflict[] };
  after: { score: PlanScore; conflicts: Conflict[]; stands: Record<string, StandAssignment> };
  diff: { moves: StandMove[]; counterChanges: number; beltChanges: number };
  elapsedMs: number;
  turnsConsidered: number;
}

export interface OptimizeDayPreview extends OptimizePreview {
  date: string;
}

export interface RecoveryOptionView {
  id: string;
  title: string;
  description: string;
  score: PlanScore;
  conflicts: Conflict[];
  moves: StandMove[];
  stands: Record<string, StandAssignment>;
}

export interface RecoveryPreview {
  baseVersionId: number;
  disruption: string;
  doNothing: { score: PlanScore; conflicts: Conflict[] };
  options: RecoveryOptionView[];
  turns: Turn[];
  closures: StandClosure[];
  elapsedMs: number;
}

export interface DayReport {
  date: string;
  flights: number;
  arrivals: number;
  departures: number;
  cancelled: number;
  onTimePct: number | null;
  avgDelayMin: number | null;
  planVersions: number;
  pax: number | null;
  /** Passengers through the security checkpoint (BPSS2); null or absent when it was not read. */
  screened?: number | null;
}

/** One day at the security checkpoint, from BPSS2's scans. */
export interface SecurityDay {
  date: string;
  /** Null when nothing was recorded that day, which is not the same as nobody. */
  screened: number | null;
  /** Passengers per clock hour, airport time, 0 to 23. */
  hours: number[] | null;
  peak: { at: string; scans: number } | null;
}

/** When a set of departures' passengers cleared security, T-240 first, as shares summing to 100. */
export interface ShowUpCurve {
  share: number[];
  /** Minutes before departure by which half, and four in five, had cleared. */
  half: number | null;
  most: number | null;
  departures: number;
  passengers: number;
}

export interface SecurityHistory {
  from: string;
  to: string;
  days: SecurityDay[];
  totals: {
    screened: number | null;
    daysMeasured: number;
    busiestDay: { date: string; screened: number } | null;
    peak: { at: string; scans: number; perHour: number } | null;
  };
  curves: { all: ShowUpCurve; D: ShowUpCurve; T: ShowUpCurve; I: ShowUpCurve };
  airlines: Array<{ airline: string; iata: string | null; name: string; curve: ShowUpCurve }>;
  setAside: number;
  /** Queue waits; null until any were recorded. */
  wait?: WaitHistory | null;
}

/** One day of the forecast review (apps/server/src/forecast.ts). */
export interface ReviewDay {
  date: string;
  madeAt: string;
  leadHours: number;
  forecast: number;
  actual: number | null;
  deltaPct: number | null;
  peak: { forecast: { at: string; passengers: number } | null; actual: { at: string; passengers: number } | null };
  flights: { scored: number; medianErrorPct: number | null };
  basis: Record<string, number> | null;
  notes: Array<{ kind: "holiday" | "travel" | "cancelled" | "data" | "staff"; text: string; by?: string }>;
  rolling7?: number | null;
}

export interface ForecastReview {
  from: string;
  to: string;
  days: ReviewDay[];
  summary: { scored: number; meanAbsErrorPct: number | null; last7: number | null; previous7: number | null; withinTenPct: number | null };
}

export type AnomalyDecision = "keep" | "change" | "other";

/** One kind of anomaly, what the system does about it, and a duty manager's word on it. */
export interface Anomaly {
  kind: string;
  title: string;
  seen: string;
  now: string;
  question: string;
  proposal: string;
  watched: boolean;
  decision: { decision: AnomalyDecision; note: string; by: string; at: string } | null;
  events: Array<{ id: number; date: string; flights: string; detail: string; firstSeen: string; lastSeen: string; count: number }>;
  recent: number;
}

export interface SandboxInfo {
  id: number;
  slot: 1 | 2;
  name: string;
  status: "pending" | "active" | "archived";
  createdBy: string;
  approvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  basedOn: number | null;
}

export interface SandboxProposal {
  id: string;
  sandboxId: number;
  summary: string;
  status: "open" | "applied" | "dropped";
  createdBy: string;
  createdAt: string;
  diff: ConfigChange[];
}

/** A sandbox's plan for a day beside what the cameras saw (server: sandbox-actuals.ts). */
export interface SandboxActualRow {
  turnId: string;
  label: string;
  arrival: string | null;
  departure: string | null;
  registration: string | null;
  planStandId: string | null;
  planStand: string | null;
  status: "cameras" | "aodb" | "no_camera" | "manual" | "differs";
  camera: { turnId: string; standId: string | null; stand: string; state: string; onBlockAt: string | null; offBlockAt: string | null; stillUrl: string | null; clipUrl: string | null } | null;
  aodb: { onBlockAt: string | null; offBlockAt: string | null; standId: string | null; stand: string | null };
}
export interface SandboxActuals {
  date: string;
  rows: SandboxActualRow[];
  counts: { flights: number; cameras: number; aodb: number; noCamera: number; manual: number; differs: number };
  unplaced: Array<{ turnId: string; stand: string; arrival: string | null; departure: string | null; onBlockAt: string | null }>;
}

export interface SandboxFigures {
  score: PlanScore;
  jetsOffBridge: number;
  pushbackPairs: number;
  unallocated: number;
}

export interface SandboxComparison {
  from: string;
  to: string;
  days: Array<{ date: string; when: "past" | "ahead"; live: SandboxFigures; sandbox: SandboxFigures; moved: number; aodb?: AodbAgreement }>;
  totals: { live: SandboxFigures; sandbox: SandboxFigures; moved: number; aodb?: AodbAgreement };
  diff: ConfigChange[];
}

/** Visits the AODB gated, and how many each set of rules put on the same gate. */
export interface AodbAgreement {
  gated: number;
  live: number;
  sandbox: number;
}

/** A flight the AODB keeps putting on one stand. */
export interface AodbLearned {
  flight: string;
  stand: string;
  seen: number;
  share: number;
}

export interface StandUse {
  stand: string;
  name: string;
  gate: string | null;
  jetbridge: boolean;
  turns: number;
  usedPct: number;
  avgVisitMin: number | null;
  byHour: number[];
}

export interface OpenWindow {
  from: string;
  to: string;
  minutes: number;
  kind: "bridge" | "contact";
  free: number;
  days: number;
}

/** One entry of a ranking on the Bags tab: a destination, an airline, a flight. */
export interface BagRanked {
  key: string;
  label: string;
  bags: number;
  flights: number;
  share: number;
}

export interface BagsDay {
  date: string;
  bags: number;
  flights: number;
  perFlight: number | null;
  departures: number;
  arrivals: number;
  connecting: number | null;
  direct: number | null;
  connectingPct: number | null;
  perPax: number | null;
}

export interface BagsFlightRow {
  flight: string;
  airline: string;
  airlineName: string;
  direction: "ARR" | "DEP";
  airport: string | null;
  operations: number;
  bags: number;
  perOperation: number;
  connecting: number | null;
  connectingPct: number | null;
  pax: number | null;
  perPax: number | null;
}

/** GET /api/reports/bags: bags over a range, from the bag system's counts. */
export interface BagsHistory {
  from: string;
  to: string;
  daysWithBags: number;
  totals: {
    bags: number;
    flights: number;
    perFlight: number | null;
    departures: number;
    arrivals: number;
    connecting: number | null;
    direct: number | null;
    connectingPct: number | null;
    connectingReported: number;
    perPax: number | null;
    paxBasis: { bags: number; pax: number; flights: number };
    unmatched: number;
  };
  byHour: number[];
  top: {
    destinations: BagRanked[];
    origins: BagRanked[];
    connections: BagRanked[];
    airlines: BagRanked[];
    flights: BagRanked[];
    finalDestinations: BagRanked[];
    connectingDestinations: BagRanked[];
    connectingVia: BagRanked[];
  };
  destinationsReported: number;
  unusedFields: string[];
  days: BagsDay[];
  flights: BagsFlightRow[];
  lastAt: string | null;
}

export interface GateHistory {
  from: string;
  to: string;
  days: number;
  openFromHour: number;
  openToHour: number;
  stands: StandUse[];
  apronByHour: number[];
  windows: OpenWindow[];
}

export interface WaitDay {
  date: string;
  passengers: number;
  avgSec: number;
  /** Over the open hours only: first screening to last passenger, a quiet quarter hour counting as no wait. */
  openAvgSec: number | null;
  maxSec: number;
  maxAt: string | null;
  from: string | null;
  to: string | null;
  overPct: number;
}

export interface WaitHistory {
  days: WaitDay[];
  passengers: number;
  avgSec: number;
  openAvgSec: number | null;
  max: { sec: number; at: string } | null;
  last: { sec: number; at: string } | null;
  overPct: number;
  targetMin: number;
  byHour: Array<number | null>;
  bands: number[];
  bandEdgesMin: number[];
  since: string | null;
}

/** A figure and how many operations it was measured across, so it can be weighed. */
export interface Measured {
  total: number;
  from: number;
}

export interface FlightHistoryRow {
  flight: string;
  airline: string;
  iata: string | null;
  airlineName: string;
  direction: "ARR" | "DEP";
  sector: string;
  operations: number;
  cancelled: number;
  seats: number | null;
  aircraftType: string | null;
  /** The feed never said what flew; the type is the one assumed from the airline and flight number. */
  aircraftTypeAssumed?: boolean;
  s2: Measured;
  airlinePax: Measured;
  bags: Measured;
  delay: Measured;
  /** Waiting on Vectro Apron. */
  fuelKg: null;
  deiceMin: null;
}

/** One flight on one day; null where nothing was measured. */
export interface FlightSeriesDay {
  date: string;
  cancelled: boolean;
  seats: number | null;
  airlinePax: number | null;
  s2: number | null;
  bags: number | null;
  delayMin: number | null;
}

export interface FlightSeries {
  flight: string;
  direction: "ARR" | "DEP";
  /** Seats on the type it usually flies, the same figure as the table's row. */
  seats: number | null;
  days: FlightSeriesDay[];
}

/** Every rule the stand allocator uses, built from the live settings. */
export interface RulesView {
  order: string[];
  hard: Array<{ code: string; rule: string; cost?: string; from: string }>;
  soft: Array<{ code: string; rule: string; cost?: string; from: string }>;
  preferences: Array<{ id: string; label: string; who: string; aircraft: string; prefers: string; weight: number; learned: boolean }>;
  stands: Array<{
    id: string;
    name: string;
    gate: string | null;
    kind: string;
    jetbridge: boolean;
    maxWingspan: number;
    international: string | null;
    domesticExcluded: boolean;
    allowedAirlines: string[];
    allowedTypes: string[];
    autoAllocate: boolean;
    active: boolean;
    blocks: Array<{ stand: string; whenTypes: string[]; text: string }>;
    blockedBy: Array<{ stand: string; whenTypes: string[]; text: string }>;
  }>;
  numbers: Record<string, number | boolean | null>;
}

export interface VersionRow {
  id: number;
  parentId: number | null;
  kind: string;
  summary: string;
  score: PlanScore | null;
  createdBy: string | null;
  createdAt: string;
}

export const api = {
  session: () =>
    call<{
      user: User | null;
      /** Password accepted; waiting for the authenticator code ("mfa") or for it to be set up ("setup"). */
      pending: { stage: "mfa" | "setup"; email: string; name: string } | null;
      /** Set when the app is running somewhere its data will not survive. */
      storageWarning: string | null;
      /** Set when the server's time zone data is behind a clock rule the airport lives under. */
      timeWarning: string | null;
      demoLogin: Array<{ id: string; name: string; role: Role; blurb: string }> | null;
    }>("GET", "/api/session"),
  login: (email: string, password: string) =>
    call<{ user?: User; mfaRequired?: boolean; mfaSetupRequired?: boolean }>("POST", "/api/login", { email, password }),
  mfaVerify: (input: { code?: string; recoveryCode?: string }) => call<{ user: User; recoveryCodesLeft?: number }>("POST", "/api/mfa/verify", input),
  mfaStatus: () => call<{ enabled: boolean; recoveryCodesLeft: number; required: boolean }>("GET", "/api/mfa"),
  mfaSetup: () => call<{ secret: string; otpauthUrl: string }>("POST", "/api/mfa/setup", {}),
  mfaEnable: (code: string) => call<{ recoveryCodes: string[] }>("POST", "/api/mfa/enable", { code }),
  mfaRecoveryCodes: (code: string) => call<{ recoveryCodes: string[] }>("POST", "/api/mfa/recovery-codes", { code }),
  mfaDisable: (code: string) => call<{ ok: boolean }>("POST", "/api/mfa/disable", { code }),
  help: () => call<HelpIndex>("GET", "/api/help"),
  helpPage: (name: string, original = false) =>
    call<{ page: string; title: string; markdown: string }>("GET", `/api/help/page?name=${encodeURIComponent(name)}${original ? "&original=1" : ""}`),
  helpAsk: (question: string, history: Array<{ role: "user" | "assistant"; content: string }> = []) =>
    call<HelpAnswer>("POST", "/api/help/ask", { question, history }),
  helpQuestions: () =>
    call<{
      recent: Array<{ id: number; at: string; askedBy: string | null; question: string; answered: boolean; inGuide: boolean }>;
      gaps: Array<{ question: string; times: number; lastAt: string }>;
      edited: Array<{ page: string; title: string; updatedBy: string | null; updatedAt: string | null }>;
    }>("GET", "/api/help/questions"),
  helpDraft: (instruction: string, page?: string) => call<{ markdown: string | null }>("POST", "/api/help/draft", { instruction, page }),
  helpSavePage: (page: string, title: string, markdown: string) => call<{ pages: HelpIndex["pages"] }>("PUT", "/api/help/page", { page, title, markdown }),
  helpRevertPage: (name: string) => call<{ pages: HelpIndex["pages"] }>("DELETE", `/api/help/page?name=${encodeURIComponent(name)}`),
  bags: (date: string) => call<BagDay>("GET", `/api/bags?date=${date}`),
  forecast: (date: string) => call<DayForecast>("GET", `/api/forecast?date=${date}`),
  bagCoverage: (days = 14) =>
    call<{ from: string; to: string; days: Array<{ date: string; flights: number; counted: number; bags: number }> }>("GET", `/api/bags/coverage?days=${days}`),
  reports: (from: string, to: string) =>
    call<{
      from: string;
      to: string;
      totals: DayReport;
      days: DayReport[];
      airlines: Array<{ airline: string; flights: number; onTimePct: number | null; avgDelayMin: number | null; avgTurnMin: number | null; pax: number | null }>;
      stands: Array<{ stand: string; turns: number }>;
    }>("GET", `/api/reports?from=${from}&to=${to}`),
  flightHistory: (q: { from: string; to: string; airline?: string; flight?: string; direction?: "ARR" | "DEP" }) =>
    call<{ from: string; to: string; rows: FlightHistoryRow[]; airlines: Array<{ code: string; iata: string | null; name: string }> }>(
      "GET",
      `/api/reports/flights?${new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString()}`,
    ),
  /** One flight day by day, for the chart under its row. */
  flightSeries: (q: { from: string; to: string; flight: string; direction: "ARR" | "DEP" }) =>
    call<FlightSeries>("GET", `/api/reports/flights/series?${new URLSearchParams(q).toString()}`),
  securityHistory: (from: string, to: string) => call<SecurityHistory>("GET", `/api/reports/security?from=${from}&to=${to}`),
  gateHistory: (from: string, to: string) => call<GateHistory>("GET", `/api/reports/gates?from=${from}&to=${to}`),
  bagsHistory: (from: string, to: string) => call<BagsHistory>("GET", `/api/reports/bags?from=${from}&to=${to}`),
  forecastReview: (from: string, to: string) => call<ForecastReview>("GET", `/api/forecast/review?from=${from}&to=${to}`),
  setForecastNote: (date: string, note: string) => call<{ ok: true }>("PUT", "/api/forecast/notes", { date, note }),
  sandboxes: () => call<{ sandboxes: SandboxInfo[]; canApprove: boolean; canDesign: boolean }>("GET", "/api/sandboxes"),
  createSandbox: (name: string) => call<SandboxInfo>("POST", "/api/sandboxes", { name }),
  aodbLearned: (id: number) => call<{ flights: AodbLearned[]; onLive: string[] }>("GET", `/api/sandboxes/${id}/aodb-learned`),
  importAodb: (id: number, stands: string[]) => call<{ id: number; added: number }>("POST", `/api/sandboxes/${id}/aodb-learned/import`, { stands, confirm: true }),
  approveSandbox: (id: number) => call<SandboxInfo>("POST", `/api/sandboxes/${id}/approve`, {}),
  archiveSandbox: (id: number) => call<{ ok: true }>("DELETE", `/api/sandboxes/${id}`),
  sandbox: (id: number) =>
    call<{ sandbox: SandboxInfo; diff: ConfigChange[]; versions: Array<{ id: number; note: string; by: string; at: string }>; proposals: SandboxProposal[] }>("GET", `/api/sandboxes/${id}`),
  restoreSandbox: (id: number, versionId: number) => call<{ ok: true }>("POST", `/api/sandboxes/${id}/restore`, { versionId }),
  applyProposal: (id: number, pid: string) => call<{ ok: true }>("POST", `/api/sandboxes/${id}/proposals/${pid}/apply`, {}),
  dropProposal: (id: number, pid: string) => call<{ ok: true }>("POST", `/api/sandboxes/${id}/proposals/${pid}/drop`, {}),
  sandboxActuals: (id: number, date: string) => call<SandboxActuals>("GET", `/api/sandboxes/${id}/actuals?date=${date}`),
  compareSandbox: (id: number) => call<SandboxComparison>("POST", `/api/sandboxes/${id}/compare`, {}),
  promoteSandbox: (id: number, note: string) => call<{ id: number }>("POST", `/api/sandboxes/${id}/promote`, { note, confirm: true }),
  talkSandbox: (id: number, message: string, history: Array<{ role: "user" | "assistant"; content: string }>) =>
    call<{ answer: string | null; proposals: SandboxProposal[]; looked: string[] }>("POST", `/api/sandboxes/${id}/talk`, { message, history }),
  schedule: (from: string, to: string) =>
    call<{ from: string; to: string; flights: Array<{ id: string; date: string; source: string; note: string; leg: FlightLeg }> }>("GET", `/api/schedule?from=${from}&to=${to}`),
  buildFlight: (f: {
    airline: string;
    flightNumber: string;
    direction: "ARR" | "DEP";
    localTime: string;
    otherAirport: string | null;
    aircraftType: string | null;
    registration: string | null;
    sector: "D" | "T" | "I";
    seats: number | null;
  }) => call<{ id: string }>("POST", "/api/schedule/flight", f),
  addStoredAircraft: (a: { registration: string; aircraftType: string | null; airline: string | null; note?: string }) =>
    call<{ id: string }>("POST", "/api/schedule/stored", a),
  removeScheduled: (id: string) => call<{ ok: boolean }>("DELETE", `/api/schedule/${encodeURIComponent(id)}`),
  /** Swap with an aircraft on the board, or bring a tail in from the depot and swap with that. */
  swapTails: (
    date: string,
    turnId: string,
    choice: { withTurnId: string } | { newTail: { registration: string; aircraftType: string | null; airline: string | null } },
  ) => call<{ summary: string }>("POST", "/api/ops/swap", { date, turnId, ...choice }),
  splitTurn: (date: string, turnId: string) => call<{ summary: string }>("POST", "/api/ops/split", { date, turnId }),
  /** The aircraft is not what the feed says. A real change, not a what-if. */
  changeType: (date: string, turnId: string, aircraftType: string, note?: string) =>
    call<{ id: string; summary: string }>("POST", "/api/ops/type", { date, turnId, aircraftType, note }),
  /** Take a flight off the board, whatever the feed keeps saying. Restored from History (removeEvent). */
  removeFlight: (date: string, turnId: string, note?: string) => call<{ id: string; summary: string }>("POST", "/api/ops/remove", { date, turnId, note }),
  /**
   * Cancel one leg: it goes to the C1 row and frees its stand, counters and belts. With
   * `bags`, a departure's checked bags come back to a reclaim belt. Undone with removeEvent.
   */
  cancelFlight: (date: string, legId: string, bags: { from: number; minutes: number } | null, note?: string) =>
    call<{ id: string; summary: string }>("POST", "/api/ops/cancel", { date, legId, bags, note }),
  /** A cancelled departure's bags at another time or for longer, or none after all. */
  setCancelBags: (date: string, eventId: string, bags: { from: number; minutes: number } | null) =>
    call<{ id: string | null; summary: string }>("PUT", `/api/ops/cancel/${encodeURIComponent(eventId)}/bags`, { date, bags }),
  rules: () => call<RulesView>("GET", "/api/rules"),
  /** The audit trail in words, with the revision each change made. Administrators. */
  changes: (q: { from?: string; to?: string; kind?: ChangeKind; user?: string } = {}) =>
    call<{ rows: ChangeRow[] }>("GET", `/api/changes?${new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString()}`),
  settingsDiff: (id: number) => call<SettingsDiff>("GET", `/api/settings/versions/${id}/diff`),
  audit: (q: { action?: string; user?: string } = {}) =>
    call<{ rows: Array<{ id: number; at: string; action: string; user: string | null; detail: unknown }>; actions: string[] }>(
      "GET",
      `/api/audit?${new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString()}`,
    ),
  users: () =>
    call<{ mfaRequired: boolean; users: Array<User & { mfa: boolean; created_at: string; last_login: string | null }> }>("GET", "/api/users"),
  resetMfa: (id: string) => call<{ ok: boolean }>("POST", `/api/users/${encodeURIComponent(id)}/mfa-reset`, {}),
  resetPassword: (id: string) => call<{ emailed: boolean; message: string; link: string | null }>("POST", `/api/users/${encodeURIComponent(id)}/reset-password`, {}),
  deleteUser: (id: string) => call<{ ok: boolean }>("DELETE", `/api/users/${encodeURIComponent(id)}`),
  addUser: (u: { name: string; email: string; role: Role; airline?: string | null; apiScopes?: Scope[] }) =>
    call<{ user: User; emailed: boolean; message: string; link: string | null }>("POST", "/api/users", u),
  sendInvite: (id: string) => call<{ emailed: boolean; message: string; link: string | null }>("POST", `/api/users/${encodeURIComponent(id)}/invite`, {}),
  setRole: (id: string, role: Role, airline?: string | null, apiScopes?: Scope[]) =>
    call<{ ok: boolean; role: Role; apiScopes: Scope[]; keysRevoked: number }>("PATCH", `/api/users/${encodeURIComponent(id)}/role`, { role, airline, apiScopes }),
  /** A vendor's own keys, allowance and the documentation links; the secret is returned once, on creation. */
  myKeys: () => call<VendorKeys>("GET", "/api/keys/mine"),
  createMyKey: (name: string, scopes: Scope[]) => call<{ key: string; row: ApiKeyRow }>("POST", "/api/keys/mine", { name, scopes }),
  revokeMyKey: (id: number) => call<{ ok: boolean }>("DELETE", `/api/keys/mine/${id}`, {}),
  emailStatus: () => call<{ configured: boolean; state: string; message: string; lastSend: number | null; sent: number }>("GET", "/api/email"),
  emailTest: () => call<{ ok: boolean; message: string }>("POST", "/api/email/test", {}),
  invite: (token: string) => call<{ name: string; email: string }>("GET", `/api/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string) => call<{ ok: boolean; email: string }>("POST", `/api/invite/${encodeURIComponent(token)}`, { password }),
  demoLogin: (id: string) => call("POST", "/api/demo-login", { id }),
  logout: () => call("POST", "/api/logout", {}),
  config: () => call<AirportConfig>("GET", "/api/config"),
  board: (date?: string) => call<Board>("GET", `/api/board${date ? `?date=${date}` : ""}`),
  versions: (date: string) => call<VersionRow[]>("GET", `/api/versions?date=${date}`),
  explain: (date: string, turnId: string) =>
    call<Explanation>("GET", `/api/explain?date=${date}&turnId=${encodeURIComponent(turnId)}`),
  optimizePreview: (date: string) => call<OptimizePreview>("POST", "/api/optimize/preview", { date }),
  /** The same optimization for several days from `date`, one preview each; nothing changes. */
  optimizePreviewDays: (date: string, days: number) => call<{ days: OptimizeDayPreview[] }>("POST", "/api/optimize/preview-days", { date, days }),
  applyDays: (days: { date: string; baseVersionId: number; stands: Record<string, StandAssignment>; summary: string }[]) =>
    call<{ days: { date: string; versionId: number | null; moves: number; reworked: boolean }[] }>("POST", "/api/plan/apply-days", { days }),
  applyStands: (date: string, baseVersionId: number, stands: Record<string, StandAssignment>, summary: string) =>
    call<{ versionId: number; score: PlanScore }>("POST", "/api/plan/apply", { date, baseVersionId, stands, summary }),
  move: (date: string, baseVersionId: number, turnId: string, standId: string | null, locked?: boolean) =>
    call<{ versionId: number; score: PlanScore }>("POST", "/api/plan/move", { date, baseVersionId, turnId, standId, locked }),
  /** Why a planner moved a flight; the learning reads it. */
  moveReason: (date: string, turnId: string, standId: string | null, reason: string) =>
    call<{ ok: true }>("POST", "/api/plan/move-reason", { date, turnId, standId, reason }),
  /** Back `steps` versions as one new version: a change made as several (a merged counter bar) comes back whole. */
  undo: (date: string, steps = 1) => call<{ versionId: number }>("POST", "/api/plan/undo", steps > 1 ? { date, steps } : { date }),
  recoveryPreview: (date: string, disruption: Disruption) =>
    call<RecoveryPreview>("POST", "/api/recovery/preview", { date, disruption }),
  recoveryApply: (date: string, baseVersionId: number, disruption: Disruption, optionId: string) =>
    call<{ versionId: number; score: PlanScore }>("POST", "/api/recovery/apply", { date, baseVersionId, disruption, optionId }),
  removeClosure: (id: string) => call("DELETE", `/api/closures/${encodeURIComponent(id)}`, {}),
  setResource: (date: string, baseVersionId: number, kind: "counters" | "belts" | "outbound", legId: string, value: string[] | string | null, clear = false) =>
    call<{ versionId: number }>("POST", "/api/plan/resource", { date, baseVersionId, kind, legId, value, clear }),
  /** Who a tow would be texted to and the words, before it is planned. */
  towPreview: (date: string, turnId: string, at: number, toStand: string, note?: string) =>
    call<TowPreview>("POST", "/api/ops/tow/preview", { date, turnId, at, toStand, note }),
  tow: (date: string, turnId: string, at: number, toStand: string, note?: string) =>
    call<{ id: string; summary: string; texted: number; names: string[] }>("POST", "/api/ops/tow", { date, turnId, at, toStand, note }),
  /** Hold the stand, desks or a belt past its usual end. Taken back with removeEvent. */
  extend: (date: string, turnId: string, resource: ExtendResource, until: number, note?: string) =>
    call<{ id: string; summary: string }>("POST", "/api/ops/extend", { date, turnId, resource, until, note }),
  mech: (date: string, turnId: string, until: number, note: string) =>
    call<{ id: string; summary: string }>("POST", "/api/ops/mech", { date, turnId, until, note }),
  /** Removing a tow texts its cancellation; texted and names say to whom. */
  removeEvent: (id: string) => call<{ ok: boolean; texted: number; names: string[] }>("DELETE", `/api/ops/events/${encodeURIComponent(id)}`, {}),
  /** Every flight the board has for a run of days, at most 14. */
  flights: (from: string, to: string) => call<{ from: string; to: string; days: FlightDay[] }>("GET", `/api/flights?from=${from}&to=${to}`),
  /** How the days ahead stand: flights, where they came from, and what has no stand. */
  daysAhead: () => call<{ days: DayAhead[] }>("GET", "/api/days-ahead"),
  settings: () =>
    call<{
      config: AirportConfig;
      history: Array<{ id: number; note: string; createdBy: string; createdAt: string }>;
      learned: StandPreference[];
      /** Set when these are a sandbox's settings rather than Live's. */
      sandbox?: { id: number; name: string };
    }>("GET", "/api/settings"),
  saveSettings: (config: AirportConfig, note: string) => call<{ id: number }>("PUT", "/api/settings", { config, note }),
  dataSources: () => call<DataSources>("GET", "/api/data-sources"),
  uploadLoads: (name: string, contentBase64: string) => call<ImportLog>("POST", "/api/loads/upload", { name, contentBase64 }),
  copoptLogin: (user: string, password: string) =>
    call<{ ok: boolean; status: number; message: string }>("POST", "/api/data-sources/copopt/login", { user, password }),
  pollSheets: () => call<{ log: ImportLog[] }>("POST", "/api/loads/sheets/poll", {}),
  testEmail: (subject: string, text: string, html: string) => call<ImportLog[]>("POST", "/api/loads/email-test", { subject, text, html }),
  notifications: () => call<Notifications>("GET", "/api/notifications"),
  anomalies: () => call<{ anomalies: Anomaly[] }>("GET", "/api/anomalies"),
  decideAnomaly: (kind: string, decision: AnomalyDecision, note: string) => call<{ anomalies: Anomaly[] }>("PUT", `/api/anomalies/${encodeURIComponent(kind)}`, { decision, note }),
  /** Tow crews: who is texted when a tow is planned or cancelled, per airline. */
  handlers: () => call<{ handlers: Array<{ id: string; name: string; phone: string; airlines: string[]; active: boolean }> }>("GET", "/api/sms/handlers"),
  saveHandlers: (handlers: Array<{ name: string; phone: string; airlines: string[]; active: boolean }>) => call<{ ok: boolean }>("PUT", "/api/sms/handlers", { handlers }),
  /** Apply the workspace's newer settings version here, on purpose. */
  applyWorkspaceSettings: () => call<{ ok: boolean; note: string }>("POST", "/api/carry-over/apply-settings", {}),
  saveContacts: (contacts: Array<{ name: string; phone: string; canApprove: boolean; active: boolean }>) =>
    call<{ contacts: Contact[] }>("PUT", "/api/notifications/contacts", { contacts }),
  checkNow: () => call<{ lastAction: string | null }>("POST", "/api/notifications/check-now", {}),
  simulateReply: (phone: string, body: string) => call<{ answer: string }>("POST", "/api/notifications/simulate-reply", { phone, body }),
  usage: (days = 30) => call<UsageReport>("GET", `/api/admin/usage?days=${days}`),
  myUsage: () => call<{ keepDays: number; rows: Array<{ at: string; kind: string; path: string | null; ip: string | null }> }>("GET", "/api/me/usage"),
  integrations: () => call<IntegrationsInfo>("GET", "/api/admin/integrations"),
  /** What Vectro Apron's cameras saw on a day: turns, state, steps and the media of each moment. */
  apronTurns: (date: string) => call<{ date: string; turns: CameraTurn[] }>("GET", `/api/apron/turns?date=${date}`),
  createKey: (name: string, scopes: string[]) => call<{ key: string; row: ApiKeyRow }>("POST", "/api/admin/api-keys", { name, scopes }),
  revokeKey: (id: number) => call("DELETE", `/api/admin/api-keys/${id}`, {}),
  createWebhook: (name: string, url: string) => call<{ id: number; secret: string }>("POST", "/api/admin/webhooks", { name, url, events: ["allocation.changed"] }),
  removeWebhook: (id: number) => call("DELETE", `/api/admin/webhooks/${id}`, {}),
  testWebhook: (id: number) => call<{ status: string }>("POST", `/api/admin/webhooks/${id}/test`, {}),
  clock: (action: "play" | "pause" | "speed" | "jump", extra: { speed?: number; at?: number } = {}) =>
    call<ClockState>("POST", "/api/clock", { action, ...extra }),
  /** A problem, feature request or question for the product team; kept here and emailed. */
  sendSupport: (kind: SupportKind, title: string, details: string, page?: string) =>
    call<{ id: number; emailed: boolean; message: string }>("POST", "/api/support", { kind, title, details, page }),
  /** Your own requests; every request for an administrator. */
  supportRequests: (status?: SupportStatus) => call<{ rows: SupportRequest[] }>("GET", `/api/support${status ? `?status=${status}` : ""}`),
  updateSupport: (id: number, patch: { status?: SupportStatus; note?: string }) => call<{ row: SupportRequest }>("PATCH", `/api/support/${id}`, patch),
};

export type SupportKind = "bug" | "feature" | "question";
export type SupportStatus = "new" | "seen" | "done";

/** One request on the Support page, as the server keeps it (apps/server/src/support.ts). */
export interface SupportRequest {
  id: number;
  at: string;
  user: { id: string | null; name: string | null; email: string | null };
  role: string | null;
  kind: SupportKind;
  title: string;
  details: string;
  page: string | null;
  context: { userAgent: string | null; appVersion: string; airport: string; url: string | null } | null;
  status: SupportStatus;
  /** What an administrator wrote back; empty until they do. */
  note: string;
  emailed: boolean;
  updatedAt: string | null;
}

export interface TowPreview {
  /** The tow crews on duty for the aircraft's airline. */
  crew: Array<{ name: string; airlines: string[] }>;
  text: string;
  /** Simulated until Twilio is set up: the text is logged and shown in Settings, not sent. */
  delivery: "twilio" | "simulated";
}

/** One flight on the Schedule screen, as the server lists it (apps/server/src/flights-view.ts). */
export interface FlightRow {
  id: string;
  turnId: string;
  flightNumber: string;
  direction: "ARR" | "DEP";
  airline: string;
  airlineName: string;
  otherAirport: string | null;
  scheduled: number;
  estimated: number | null;
  actual: number | null;
  status: FlightLeg["status"];
  aircraftType: string | null;
  registration: string | null;
  seats: number | null;
  pax: number | null;
  stored: boolean;
  source: string;
  label: string;
  stand: string | null;
  /** Set when the row was built on the Schedule screen and can be removed there. */
  scheduledId: string | null;
}

export interface FlightDay {
  date: string;
  /** The past day this one was projected from, when it is beyond the feed, or "copopt" when CopOpt's schedule supplied it. */
  projectedFrom: string | null;
  flights: FlightRow[];
}

/** One day ahead, as the server sums it up (apps/server/src/plan-ahead.ts). */
export interface DayAhead {
  date: string;
  flights: number;
  arrivals: number;
  departures: number;
  sources: { feed: number; copopt: number; projected: number; built: number };
  /** Aircraft on the board with no stand. */
  unallocated: number;
  planned: boolean;
  projectedFrom: string | null;
  /** Fewer flights than a quiet day explains; never said of an airline account's own flights. */
  thin: boolean;
}

export interface UsageReport {
  days: number;
  /** How long the trail is kept before it is deleted. */
  keepDays: number;
  people: Array<{ userId: string; name: string; role: string; lastAt: string; signIns: number; changes: number; places: string[] }>;
  daily: Array<{ day: string; people: number; signIns: number; changes: number }>;
  hours: Array<{ hour: number; events: number }>;
  failures: Array<{ at: string; name: string | null; ip: string | null; where: string | null }>;
  recent: Array<{ at: string; name: string | null; role: string | null; kind: string; path: string | null; ip: string | null; where: string | null; agent: string }>;
}

/** One quarter hour at the checkpoint, with the flights putting people in it, each with its airline. */
export interface ForecastSlot {
  at: number;
  passengers: number;
  lanesNeeded: number;
  top: Array<{ flight: string; airline: string; passengers: number }>;
}

/** One departure on the Forecast page; seats is the leg's own, else its type's, else null. */
export interface ForecastFlight {
  flight: string;
  scheduled: number;
  airline: string;
  sector: string;
  passengers: number;
  seats: number | null;
  basis: string;
  from: number;
  bags: number | null;
}

export interface DayForecast {
  date: string;
  madeAt: number;
  passengers: number;
  flights: ForecastFlight[];
  slots: ForecastSlot[];
  busiest: ForecastSlot | null;
  /** What actually cleared security, quarter hour by quarter hour, on the slots' clock. */
  actual: Array<{ at: number; passengers: number }>;
  /** Forecast against actual over the measured window; delta is forecast minus actual. */
  comparison: { slots: number; forecast: number; actual: number; delta: number; deltaPct: number | null } | null;
  capacity: { perLaneHour: number; basis: string; lanes: number; checkpointPerHour: number; observedPeakPerHour: number | null; note: string };
  /** What the whole thing rests on: days measured, flights, passengers, and curves set aside as incomplete readings. */
  coverage: { days: number; flights: number; passengers: number; since: string | null; excluded: number };
  accuracy: Array<{ date: string; forecast: number; actual: number; deltaPct: number | null; madeAt: string }>;
}

export interface BagDay {
  date: string;
  /** How much of the day the bag system is actually covering. */
  coverage?: {
    flights: number;
    counted: number;
    overdue: number;
    missing: Array<{ flight: string; direction: string; scheduled: number; airline: string; overdue: boolean }>;
    /** Counts filed against a flight number this airport does not have that day. */
    unmatched: Array<{ flight: string; bags: number }>;
  };
  /** False until the local bag system has reported anything for this day. */
  receiving: boolean;
  lastAt: string | null;
  totals: { bags: number; flights: number; byStage: Record<string, number> };
  flights: Array<{
    flight: string;
    direction: string;
    bags: number;
    stages: Record<string, { bags: number; at: string | null }>;
    /** Every count reported for this flight, in order, so the flow can be drawn. */
    reports: Array<{ at: string; stage: string; count: number }>;
  }>;
}

export interface HelpSource {
  page: string;
  pageTitle: string;
  heading: string;
  body: string;
  anchor: string;
  score: number;
}

export interface HelpIndex {
  ready: boolean;
  /** True when answers are written by the assistant rather than quoted from the guide. */
  written: boolean;
  pages: Array<{ page: string; title: string; headings: string[]; edited: boolean }>;
  gaps: Array<{ question: string; times: number; lastAt: string }>;
}

export interface HelpAnswer {
  answer: string | null;
  written: boolean;
  /** What the assistant checked, in order: the guide, the plan, a flight, the settings. */
  looked: string[];
  sources: HelpSource[];
  inGuide: boolean;
}

export interface ImportLog {
  at: number;
  source: string;
  ref: string;
  records: number;
  matched: number;
  skipped: number;
  note: string;
  /** First and last operating day the records carry; null when none carried a date. */
  dates: { from: string; to: string } | null;
  /** The airlines the records were filed under, after aliases such as WR to WS. */
  carriers: string[];
  flightsOnBoard: number;
}

export interface DataSources {
  copopt: { configured: boolean; state: string; message: string; lastCheck: number | null; lastSuccess: number | null } | null;
  /** `days` is how many days of schedule the last read holds, from today. */
  copoptSchedule: { state: string; message: string; at: number | null; flights: number; days: number } | null;
  carryOver: {
    mode: "off" | "export" | "import";
    state: string;
    message: string;
    at: number | null;
    database: "postgres" | "embedded";
    /** A newer settings version prepared in the workspace, for an administrator to apply. */
    pendingSettings?: { note: string; createdAt: string } | null;
  } | null;
  sheets: { sheets: Array<{ id: string; name: string; url: string; enabled: boolean; airline?: string | null }>; pollMinutes: number };
  log: ImportLog[];
  recent: Array<{ id: number; flight: string; date: string | null; direction: string | null; pax: number | null; bags: number | null; source: string; receivedAt: number }>;
  imap: { configured: boolean; lastRun: number | null; lastError: string | null; processed: number; mailbox: string | null; onlyTo: string | null };
  inboundEmail: { enabled: boolean; path: string | null };
  bpss2: {
    mode: string;
    state: string;
    message: string;
    lastRun: number | null;
    lastSuccess: number | null;
    flights: number;
    /** True when the last read hit BPSS2's 5,000-row limit, so the oldest scans were missing from it. */
    partial: boolean;
    /** True when that loss is real: nothing read earlier today holds what was cut off, so early flights may be counted short. */
    short: boolean;
  };
  bags: { state: "off" | "ok" | "quiet"; message: string; at: number | null } | null;
  security: {
    state: "off" | "ok";
    message: string;
    at: number | null;
    capacity: {
      perLaneHour: number;
      /** Never "measured": a single lane cannot be measured from the data we trust. */
      basis: "set" | "assumed";
      lanes: number;
      checkpointPerHour: number;
      observedPeakPerHour: number | null;
      observedPeakAt: string | null;
      blocks: number;
      note: string;
    };
  } | null;
}

export interface Contact {
  id: number;
  name: string;
  phone: string;
  canApprove: boolean;
  active: boolean;
}

export interface Notifications {
  live: boolean;
  contacts: Contact[];
  log: Array<{ id: number; direction: "in" | "out"; phone: string; name: string | null; body: string; status: string; at: string }>;
  approvals: Array<{
    id: string;
    date: string;
    conflictId: string;
    message: string;
    status: string;
    options: Array<{ n: number; title: string; moves: Array<{ label: string; from: string | null; to: string | null }> }>;
    createdAt: string;
    expiresAt: string;
    resolvedBy: string | null;
    result: string | null;
  }>;
  resolver: { lastRun: number | null; lastAction: string | null };
  replyUrl: string;
  /** Whether Twilio forwards replies to this app; null where it is not checked (the workspace, or not a planner). */
  replyRoute?: { state: "ok" | "wrong" | "unknown" | "off"; url: string | null; note?: string } | null;
}

export interface ApiKeyRow {
  id: number;
  name: string;
  prefix: string;
  scopes: Scope[];
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** The vendor account that issued the key for itself; null for keys administrators issue. */
  owner: { id: string; name: string | null } | null;
}

export interface VendorKeys {
  apiVersion: string;
  scopes: Scope[];
  keys: ApiKeyRow[];
  docs: string;
  spec: string;
  publicUrl: string;
}

export interface IntegrationsInfo {
  apiVersion: string;
  scopes: string[];
  keys: ApiKeyRow[];
  webhooks: Array<{ id: number; name: string; url: string; events: string[]; active: boolean; lastStatus: string | null; lastSentAt: string | null }>;
  apron: {
    lastReceived: number | null;
    alerts: Array<{ id: string; severity: string; status: string; category: string; at: string }>;
    health: Record<string, { status: string; at: string }>;
    /** Turn status and media (contract 1.1). */
    cameras?: {
      turns: number;
      live: number;
      lastTurnAt: string | null;
      media: { count: number; bytes: number; lastReceived: string | null };
      lastHeard: string | null;
      retention: { mediaDays: number; turnDays: number; maxBytes: number };
      /** Today's camera turns that meet no flight on the board: their pictures show nowhere. */
      unmatched?: Array<{ turnId: string; flights: string; date: string; stand: string | null; media: number }>;
    };
  };
  publicUrl: string;
}
